import { dispatch, type DispatchOptions } from './dispatch.ts'
import type { EventMeta, EventProvider, GlobalContext } from './types.ts'

type EventRegistry = Record<
  string,
  EventMeta & { contexts: readonly string[]; fields: unknown }
>

type HasContextOverlap<
  EventContexts extends readonly string[],
  ActiveContext extends string,
> = EventContexts[number] & ActiveContext extends never ? never : true

type EventTrackFn<Event> = Event extends { fields: infer Fields }
  ? keyof Fields extends never
    ? () => void
    : (data: Fields) => void
  : () => void

type TrackFunctions<Events, ActiveContext extends string> = {
  [
    EventName in keyof Events as Events[EventName] extends {
      contexts: readonly string[]
    }
      ? HasContextOverlap<
          Events[EventName]['contexts'],
          ActiveContext
        > extends never
        ? never
        : EventName
      : never
  ]: EventTrackFn<Events[EventName]>
}

/**
 * Whether provider failures should be logged in the console.
 *
 * Consumers compile this library from source and may not include Node types, so accessing a bare
 * `process` global would cause a TypeScript error. Browser bundles that provide `process.env` are
 * considered development environments unless `NODE_ENV` is `"production"`; bundles without it
 * disable failure logging.
 */
const isDevelopmentEnvironment = (): boolean => {
  const environment = (
    globalThis as {
      process?: { env?: Record<string, string | undefined> }
    }
  ).process?.env
  return environment !== undefined && environment.NODE_ENV !== 'production'
}

export interface ClientOptions<Events, Context extends string> {
  contexts: Context[]
  events: Events
  providers: EventProvider[]
  globalContext: () => GlobalContext
  disabled: () => boolean
  /** Optional debug predicate; when it returns true, every dispatched event is logged to the console. */
  debug?: () => boolean
}

export function createClient<
  Events extends EventRegistry,
  Context extends string,
>(options: ClientOptions<Events, Context>): TrackFunctions<Events, Context> {
  const dispatchOptions: DispatchOptions = {
    providers: options.providers,
    globalContext: options.globalContext,
    disabled: options.disabled,
    debug: options.debug,
    isDev: isDevelopmentEnvironment(),
  }

  const contextSet = new Set<string>(options.contexts)
  const client: Record<string, (data?: Record<string, unknown>) => void> = {}

  for (const [key, eventDef] of Object.entries(options.events)) {
    const hasOverlap = eventDef.contexts.some((ctx: string) =>
      contextSet.has(ctx),
    )
    if (!hasOverlap) continue

    client[key] = (data: Record<string, unknown> = {}) => {
      dispatch(eventDef, data, dispatchOptions)
    }
  }

  return client as TrackFunctions<Events, Context>
}
